import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner } from "reactstrap";
import { getStoreProjectInstallation } from "../service";
import "./StoreInstallationPrint.css";

const PATH_SEPARATOR = " -> ";

const formatPath = (path) => {
  if (!Array.isArray(path) || path.length === 0) return null;

  return path.join(PATH_SEPARATOR);
};

const formatProductCaption = (item) => {
  const title = item.catalog_name || item.name;
  const height = item.height;
  const width = item.width;
  const depth = item.depth;

  if (height == null && width == null && depth == null) return title;

  return `${title} - ${height} × ${width} × ${depth} (H× W× D)`;
};

const MAX_PRODUCTS_PER_PAGE = 3;
const PANEL_MAX_ROWS_PER_TABLE = 7;
const PANEL_TABLES_PER_ROW = 3;
const MAX_PANEL_TABLES_PER_PRODUCT = 6;

// A4 layout budget (mm) for packing product rows without clipping.
const PAGE_HEIGHT_MM = 297;
const PAGE_PADDING_MM = 24;
const PAGE_CONTENT_MM = PAGE_HEIGHT_MM - PAGE_PADDING_MM;
const BREADCRUMB_MM = 8;
const ROW_GAP_MM = 2.1;
const CAPTION_BLOCK_MM = 16;
const TABLE_HEADER_MM = 5.5;
const TABLE_ROW_MM = 6;
const PANEL_AREA_PADDING_MM = 5;
const TABLE_GROUP_GAP_MM = 3;
const IMAGE_MIN_MM = 45;
const PRODUCT_ROW_BORDER_MM = 2;
const PACKING_SAFETY_MM = 5;

const chunkPanelsForTables = (panels) => {
  const tables = [];
  for (let i = 0; i < panels.length; i += PANEL_MAX_ROWS_PER_TABLE) {
    tables.push(panels.slice(i, i + PANEL_MAX_ROWS_PER_TABLE));
  }
  return tables;
};

const estimatePanelTablesHeightMm = (panelTables) => {
  if (panelTables.length === 0) return 0;

  let height = PANEL_AREA_PADDING_MM;
  for (let i = 0; i < panelTables.length; i += PANEL_TABLES_PER_ROW) {
    const group = panelTables.slice(i, i + PANEL_TABLES_PER_ROW);
    const maxRows = Math.max(...group.map((table) => table.length));
    height += TABLE_HEADER_MM + maxRows * TABLE_ROW_MM + TABLE_GROUP_GAP_MM;
  }

  return height - TABLE_GROUP_GAP_MM;
};

const PANEL_SLOT_MIN_COUNT = 6;

// One row slot on a sheet that fits exactly MAX_PRODUCTS_PER_PAGE products.
const PRODUCT_ROW_SLOT_HEIGHT_MM = (() => {
  const available = PAGE_CONTENT_MM - BREADCRUMB_MM - PACKING_SAFETY_MM;
  const gaps = (MAX_PRODUCTS_PER_PAGE - 1) * ROW_GAP_MM;
  return (available - gaps) / MAX_PRODUCTS_PER_PAGE;
})();

const MIN_PRODUCT_MAIN_HEIGHT_MM =
  PRODUCT_ROW_SLOT_HEIGHT_MM - CAPTION_BLOCK_MM - PRODUCT_ROW_BORDER_MM;

const usesMinProductRowHeight = (panels) =>
  (Array.isArray(panels) ? panels.length : 0) < PANEL_SLOT_MIN_COUNT;

const isCompactProductRow = (item) =>
  usesMinProductRowHeight(Array.isArray(item?.panels) ? item.panels : []);

const panelMainHeightMm = (panels) => {
  if (!panels.length) return 0;

  return estimatePanelTablesHeightMm(chunkPanelsForTables(panels));
};

const pagePanelMainHeightMm = (items) => {
  const heights = items
    .map((item) => {
      const panels = Array.isArray(item?.panels) ? item.panels : [];
      if (panels.length < PANEL_SLOT_MIN_COUNT) return 0;
      return panelMainHeightMm(panels);
    })
    .filter((height) => height > 0);

  return heights.length ? Math.max(...heights) : 0;
};

const resolveMainSlotMm = (panels, pageMainSlotMm = 0) => {
  const panelCount = Array.isArray(panels) ? panels.length : 0;

  if (usesMinProductRowHeight(panels)) {
    return MIN_PRODUCT_MAIN_HEIGHT_MM;
  }

  if (panelCount === 0) {
    return 0;
  }

  let mainSlot = panelMainHeightMm(panels);
  if (pageMainSlotMm > 0) {
    mainSlot = Math.max(mainSlot, pageMainSlotMm);
  }

  return mainSlot;
};

const estimateProductRowHeightMm = (item, pageMainSlotMm = 0) => {
  const panels = Array.isArray(item.panels) ? item.panels : [];

  if (usesMinProductRowHeight(panels)) {
    return PRODUCT_ROW_SLOT_HEIGHT_MM;
  }

  if (panels.length === 0) {
    return IMAGE_MIN_MM + CAPTION_BLOCK_MM + PRODUCT_ROW_BORDER_MM;
  }

  const mainMm = resolveMainSlotMm(panels, pageMainSlotMm);
  return mainMm + CAPTION_BLOCK_MM + PRODUCT_ROW_BORDER_MM;
};

const estimateSliceHeightMm = (slice) => {
  const pageMainSlotMm = pagePanelMainHeightMm(slice.map((entry) => entry.item));

  return slice.reduce(
    (sum, entry, entryIndex) =>
      sum +
      estimateProductRowHeightMm(entry.item, pageMainSlotMm) +
      (entryIndex > 0 ? ROW_GAP_MM : 0),
    0
  );
};

const splitTablesIntoRowGroups = (allTables, maxHeightMm) => {
  const groups = [];
  let tableIndex = 0;

  while (tableIndex < allTables.length) {
    let groupTables = [];

    while (tableIndex < allTables.length) {
      const candidateTables = [...groupTables, allTables[tableIndex]];
      const rowHeight =
        estimatePanelTablesHeightMm(candidateTables) +
        CAPTION_BLOCK_MM +
        PRODUCT_ROW_BORDER_MM;

      if (groupTables.length > 0 && rowHeight > maxHeightMm) break;

      groupTables = candidateTables;
      tableIndex += 1;

      if (groupTables.length >= MAX_PANEL_TABLES_PER_PRODUCT) break;
    }

    groups.push(groupTables);
  }

  return groups;
};

const flattenItemToRows = (item, pageContentMm = PAGE_CONTENT_MM - BREADCRUMB_MM - PACKING_SAFETY_MM) => {
  const panels = Array.isArray(item.panels) ? item.panels : [];
  const allTables = chunkPanelsForTables(panels);

  if (allTables.length === 0) {
    return [
      {
        ...item,
        rowKey: String(item.id ?? item.label),
        isContinuation: false,
        totalPanelCount: item.panel_count ?? 0,
      },
    ];
  }

  const tableGroups = splitTablesIntoRowGroups(allTables, pageContentMm);

  return tableGroups.map((group, index) => {
    const groupPanels = group.flat();

    return {
      ...item,
      rowKey: `${item.id ?? item.label}-panels-${index}`,
      panels: groupPanels,
      panel_count: groupPanels.length,
      isContinuation: index > 0,
      totalPanelCount: item.panel_count ?? panels.length,
    };
  });
};

const packEntriesOntoPages = (entries) => {
  const pages = [];
  let index = 0;

  while (index < entries.length) {
    const hasBreadcrumb = Boolean(entries[index]?.path?.length);
    const available =
      PAGE_CONTENT_MM -
      (hasBreadcrumb ? BREADCRUMB_MM : 0) -
      PACKING_SAFETY_MM;
    let count = Math.min(MAX_PRODUCTS_PER_PAGE, entries.length - index);

    // Pack up to 3 compact rows (0-5 panels) when they fit the sheet budget.
    let compactCount = count;
    while (compactCount > 0) {
      const compactSlice = entries.slice(index, index + compactCount);
      if (!compactSlice.every((entry) => isCompactProductRow(entry.item))) {
        break;
      }

      if (estimateSliceHeightMm(compactSlice) <= available) {
        pages.push(compactSlice);
        index += compactCount;
        compactCount = -1;
        break;
      }

      compactCount -= 1;
    }

    if (compactCount === -1) {
      continue;
    }

    while (count > 1) {
      const slice = entries.slice(index, index + count);
      const totalHeight = estimateSliceHeightMm(slice);

      if (totalHeight <= available) break;
      count -= 1;
    }

    pages.push(entries.slice(index, index + count));
    index += count;
  }

  return pages;
};

const renderPanelTableRow = (panel, panelIndex, tableKey) => (
  <tr key={`${tableKey}-${panelIndex}`}>
    <td>
      <div>{panel.assembly_label}</div>
    </td>
    <td>{panel.packet_label}</td>
  </tr>
);

const renderPanelSubTable = (panels, tableKey) => (
  <table className="store-installation-print-table mb-0">
    <thead>
      <tr>
        <th>Panel</th>
        <th>Packet</th>
      </tr>
    </thead>
    <tbody>
      {panels.map((panel, panelIndex) =>
        renderPanelTableRow(panel, panelIndex, tableKey)
      )}
    </tbody>
  </table>
);

const FolderTitlePage = ({ name }) => (
  <div className="store-installation-print-page">
    <div className="store-installation-folder-page d-flex align-items-center justify-content-center">
      <div className="store-installation-folder-title">{name}</div>
    </div>
  </div>
);

const ComponentLegendPage = ({ title, entries, notes }) => {
  const legendEntries = Array.isArray(entries) ? entries : [];
  const legendNotes = Array.isArray(notes) ? notes : [];

  return (
    <div className="store-installation-print-page store-installation-print-page--legend">
      <div className="store-installation-legend-page d-flex flex-column">
        <h1 className="store-installation-legend-title">{title || "Component Codes"}</h1>
        <table className="store-installation-print-table store-installation-legend-table mb-0">
          <thead>
            <tr>
              <th>Code</th>
              <th>Component</th>
            </tr>
          </thead>
          <tbody>
            {legendEntries.map((entry) => (
              <tr key={entry.code}>
                <td className="store-installation-legend-code">{entry.code}</td>
                <td>{entry.name}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {legendNotes.length > 0 ? (
          <div className="store-installation-legend-notes">
            {legendNotes.map((note) => (
              <p key={note} className="mb-2">
                {note}
              </p>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const ProductRow = ({ item }) => {
  const panels = Array.isArray(item.panels) ? item.panels : [];
  const caption = formatProductCaption(item);
  const panelTables = chunkPanelsForTables(panels);
  const isContinuation = Boolean(item.isContinuation);
  const totalPanelCount = item.totalPanelCount ?? item.panel_count;
  const hasPanels = panels.length > 0;
  const needsMinRowHeight = usesMinProductRowHeight(panels);
  const useFixedPanelSlot = needsMinRowHeight;
  const usePanelImageLayout = hasPanels;
  const slotMainMm = useFixedPanelSlot ? MIN_PRODUCT_MAIN_HEIGHT_MM : 0;
  const rowMinHeightMm = needsMinRowHeight ? PRODUCT_ROW_SLOT_HEIGHT_MM : null;
  const mainRef = useRef(null);
  const rightColumnRef = useRef(null);
  const imageWrapRef = useRef(null);

  const syncImageHeight = useCallback(() => {
    const heightSource =
      usePanelImageLayout || needsMinRowHeight ? mainRef.current : rightColumnRef.current;
    const imageWrap = imageWrapRef.current;
    if (!heightSource || !imageWrap) return;

    if (window.matchMedia("(max-width: 767px)").matches) {
      imageWrap.style.height = "";
      return;
    }

    imageWrap.style.height = `${heightSource.offsetHeight}px`;
  }, [usePanelImageLayout, needsMinRowHeight]);

  useEffect(() => {
    syncImageHeight();

    const heightSource =
      usePanelImageLayout || needsMinRowHeight ? mainRef.current : rightColumnRef.current;
    if (!heightSource) return undefined;

    const observer = new ResizeObserver(() => syncImageHeight());
    observer.observe(heightSource);

    const onBeforePrint = () => syncImageHeight();
    window.addEventListener("beforeprint", onBeforePrint);

    return () => {
      observer.disconnect();
      window.removeEventListener("beforeprint", onBeforePrint);
    };
  }, [
    syncImageHeight,
    usePanelImageLayout,
    needsMinRowHeight,
    panels.length,
    panelTables.length,
    item.image,
    slotMainMm,
    rowMinHeightMm,
  ]);

  return (
    <div
      className={`store-installation-product-row${
        needsMinRowHeight ? " store-installation-product-row--min-height" : ""
      }`}
      style={
        needsMinRowHeight
          ? { "--product-row-min-height": `${rowMinHeightMm}mm` }
          : undefined
      }
    >
      <div
        ref={mainRef}
        className={`store-installation-product-main${
          useFixedPanelSlot ? " store-installation-product-main--panel-slot" : ""
        }${
          usePanelImageLayout && !useFixedPanelSlot
            ? " store-installation-product-main--panel-natural"
            : ""
        }`}
        style={
          useFixedPanelSlot
            ? { "--panel-main-height": `${slotMainMm}mm` }
            : undefined
        }
      >
        <div
          ref={imageWrapRef}
          className="store-installation-product-image-wrap"
        >
          {isContinuation ? (
            <div className="store-installation-product-continued text-muted">
              Continued
            </div>
          ) : item.image ? (
            <img
              src={item.image}
              alt={caption}
              className="store-installation-product-image"
              onLoad={syncImageHeight}
            />
          ) : (
            <div className="store-installation-product-image-missing text-muted">
              No image
            </div>
          )}
        </div>
        <div
          ref={rightColumnRef}
          className="store-installation-product-right"
        >
          {panels.length > 0 ? (
            <div className="store-installation-panel-tables d-flex flex-wrap">
              {panelTables.map((tablePanels, tableIndex) =>
                renderPanelSubTable(
                  tablePanels,
                  `${item.rowKey ?? item.label}-panels-${tableIndex}`
                )
              )}
            </div>
          ) : (
            <div className="text-muted small">No panels</div>
          )}
        </div>
      </div>
      <div className="store-installation-product-footer">
        <div className="store-installation-product-caption">
          {isContinuation ? `${caption} (continued)` : caption}
        </div>
        <div className="store-installation-panel-count">
          {isContinuation
            ? `Panels ${panels.length} of ${totalPanelCount}`
            : `${totalPanelCount} panel${totalPanelCount === 1 ? "" : "s"}`}
        </div>
      </div>
    </div>
  );
};

const ProductsPage = ({ path, items }) => {
  const pathLabel = formatPath(path);

  return (
    <div className="store-installation-print-page store-installation-print-page--products">
      <div className="store-installation-products-page d-flex flex-column">
        {pathLabel ? (
          <div className="store-installation-breadcrumb">{pathLabel}</div>
        ) : null}
        <div className="store-installation-product-rows d-flex flex-column">
          {items.map((item) => (
            <ProductRow
              key={item.rowKey ?? item.id ?? item.label}
              item={item}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const StoreInstallationPrint = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.document.documentElement.classList.add("store-installation-print-active");
    window.document.body.classList.add("store-installation-print-active");

    return () => {
      window.document.documentElement.classList.remove(
        "store-installation-print-active"
      );
      window.document.body.classList.remove("store-installation-print-active");
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getStoreProjectInstallation(projectId);
        if (!cancelled) {
          setDocument(response?.data ?? null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.error?.message ||
              err?.message ||
              "Failed to load installation drawings"
          );
          setDocument(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (document?.project_name) {
      window.document.title = `${document.project_name} — Installation`;
    }
  }, [document?.project_name]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;

      event.preventDefault();
      window.close();
      navigate(`/projects/${projectId}`);
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [navigate, projectId]);

  const renderPages = useMemo(() => {
    if (!document) return [];

    const apiPages = Array.isArray(document.pages) ? document.pages : [];
    const expanded = [];
    let productBuffer = [];

    const flushProductBuffer = (keyPrefix) => {
      if (productBuffer.length === 0) return;

      const entries = productBuffer.flatMap((entry) =>
        entry.items.flatMap((item) =>
          flattenItemToRows(item).map((row) => ({ item: row, path: entry.path }))
        )
      );
      const pageChunks = packEntriesOntoPages(entries);

      pageChunks.forEach((chunk, chunkIndex) => {
        expanded.push({
          key: `${keyPrefix}-chunk-${chunkIndex}`,
          kind: "products",
          path: chunk[0]?.path ?? [],
          items: chunk.map((entry) => entry.item),
        });
      });

      productBuffer = [];
    };

    apiPages.forEach((page, pageIndex) => {
      if (page.type === "component_legend") {
        flushProductBuffer(`pre-legend-${pageIndex}`);
        expanded.push({
          key: `component-legend-${pageIndex}`,
          kind: "component_legend",
          title: page.title,
          entries: page.entries,
          notes: page.notes,
        });
        return;
      }

      if (page.type === "folder_title") {
        flushProductBuffer(`pre-folder-${pageIndex}`);
        expanded.push({
          key: `folder-title-${pageIndex}`,
          kind: "folder_title",
          name: page.name,
        });
        return;
      }

      if (page.type === "products") {
        const items = Array.isArray(page.items) ? page.items : [];
        const path = Array.isArray(page.path) ? page.path : [];

        if (items.length > 0) {
          productBuffer.push({ path, items });
        }
      }
    });

    flushProductBuffer("final");

    return expanded;
  }, [document]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner color="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning" role="alert">
          No installation data available.
        </div>
      </div>
    );
  }

  return (
    <div className="store-installation-print-print-root d-flex flex-column align-items-center">
      {renderPages.length === 0 ? (
        <div className="store-installation-print-page">
          <div className="text-muted">No cabinets in this project.</div>
        </div>
      ) : (
        renderPages.map((page) => {
          if (page.kind === "component_legend") {
            return (
              <ComponentLegendPage
                key={page.key}
                title={page.title}
                entries={page.entries}
                notes={page.notes}
              />
            );
          }

          if (page.kind === "folder_title") {
            return <FolderTitlePage key={page.key} name={page.name} />;
          }

          return (
            <ProductsPage
              key={page.key}
              path={page.path}
              items={page.items}
            />
          );
        })
      )}
    </div>
  );
};

export default StoreInstallationPrint;
